import {parsePhoneNumberFromString} from 'libphonenumber-js';

// Payment request QR: eurm:<E.164 phone>?amount=<cents>&message=<text>
// amount and message are optional. A bare phone number is accepted too.
const SCHEME = 'eurm:';

export type PaymentRequest = {
  phoneNumber: string;
  amount?: number;
  message?: string;
};

export const buildPaymentRequest = ({phoneNumber, amount, message}: PaymentRequest) => {
  const query = [];
  if (amount && amount > 0) query.push(`amount=${amount}`);
  if (message) query.push(`message=${encodeURIComponent(message)}`);
  return `${SCHEME}${phoneNumber}${query.length > 0 ? `?${query.join('&')}` : ''}`;
};

// The number alone: anything else (tel:, wa.me links, vCards, free text) is not a payment QR
const PHONE_PATTERN = /^\+?[\d\s()-]+$/;
const AMOUNT_PATTERN = /^\d+$/;

// Throws on malformed percent-encoding or a repeated key
const parseQuery = (query: string) => {
  return query.split('&').reduce<Record<string, string>>((result, pair) => {
    const separator = pair.indexOf('=');
    const key = separator === -1 ? pair : pair.slice(0, separator);
    const value = separator === -1 ? '' : pair.slice(separator + 1);
    if (!key) return result;
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      throw new Error(`Duplicate key: ${key}`);
    }
    result[key] = decodeURIComponent(value.replace(/\+/g, ' '));
    return result;
  }, {});
};

const parse = (data: string): PaymentRequest | null => {
  const value = data.trim();
  let target = value;
  let params: Record<string, string> = {};

  if (value.toLowerCase().startsWith(SCHEME)) {
    const rest = value.slice(SCHEME.length);
    const separator = rest.indexOf('?');
    const path = separator === -1 ? rest : rest.slice(0, separator);
    const query = separator === -1 ? '' : rest.slice(separator + 1);
    target = decodeURIComponent(path);
    params = parseQuery(query);
  }

  if (!PHONE_PATTERN.test(target)) return null;
  const phone = parsePhoneNumberFromString(target);
  if (!phone || !phone.isValid()) return null;

  const request: PaymentRequest = {phoneNumber: phone.number};

  if (params.amount !== undefined) {
    if (!AMOUNT_PATTERN.test(params.amount)) return null;
    const amount = Number(params.amount);
    if (amount <= 0) return null;
    request.amount = amount;
  }
  if (params.message) {
    request.message = params.message.slice(0, 200);
  }
  return request;
};

export const parsePaymentRequest = (data: string): PaymentRequest | null => {
  try {
    return parse(data);
  } catch (e) {
    return null;
  }
};
